import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const PageContainer = styled.div`
  padding: 0 0 3rem;
`;

const ContentSection = styled.div`
  background-color: white;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const SectionTitle = styled.h2`
  color: var(--dark);
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #f0f0f0;
`;

const SubsectionTitle = styled.h3`
  color: var(--dark);
  margin: 1.5rem 0 1rem;
  font-size: 1.3rem;
`;

const LastUpdated = styled.p`
  text-align: center;
  color: var(--gray);
  font-style: italic;
  margin-bottom: 2rem;
`;

const TermsAndConditionsPage = () => {
  // SEO metadata
  const seoData = {
    title: "Terms and Conditions | Dozzy.au",
    description: "Read the terms and conditions for using Dozzy.au's AI-powered sleep consultant services for babies and toddlers.",
    keywords: [
      "terms and conditions", 
      "legal terms", 
      "Dozzy terms",
      "sleep consultant terms",
      "service agreement",
      "user agreement"
    ]
  };

  return (
    <>
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonicalUrl="https://dozzy.au/terms-and-conditions"
        ogType="website"
      />
      
      {/* Page Header */}
      <section className="py-5 bg-primary text-white text-center">
        <div className="container py-2">
          <h1>Terms and Conditions</h1>
          <p className="mx-auto" style={{ maxWidth: '700px' }}>
            Please read these terms and conditions carefully before using Dozzy.au
          </p>
        </div>
      </section>
      
      <div className="container">
        <PageContainer>
          <LastUpdated>Last Updated: May 22, 2025</LastUpdated>
          
          <ContentSection>
            <SectionTitle>1. Introduction</SectionTitle>
            <p>Welcome to Dozzy.au ("we," "our," or "us"). These Terms and Conditions ("Terms") govern your use of the Dozzy.au website and application (collectively, the "Service").</p>
            <p>By accessing or using the Service, you agree to be bound by these Terms. If you disagree with any part of the Terms, you may not access the Service.</p>
            <p>Dozzy.au is an AI-powered platform that helps parents create personalized sleep plans for their children based on guidelines from respected Australian organizations including Triple P, Rednose, Raising Children Network, and Tresillian.</p>
          </ContentSection>
          
          <ContentSection>
            <SectionTitle>2. Use of the Service</SectionTitle>
            
            <SubsectionTitle>2.1 Age Restrictions</SubsectionTitle>
            <p>The Service is intended for use by individuals who are at least 18 years of age. By using the Service, you represent and warrant that you are at least 18 years old. If you are under 18 years old, you may not use the Service.</p>
            
            <SubsectionTitle>2.2 Account Registration</SubsectionTitle>
            <p>To access certain features of the Service, you may be required to register for an account. You agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate, current, and complete.</p>
            <p>You are responsible for safeguarding the password that you use to access the Service and for any activities or actions under your password. We encourage you to use a strong password (a combination of upper and lower case letters, numbers, and symbols) for your account.</p>
            
            <SubsectionTitle>2.3 Prohibited Uses</SubsectionTitle>
            <p>You agree not to use the Service:</p>
            <ul>
              <li>In any way that violates any applicable federal, state, local, or international law or regulation</li>
              <li>To transmit, or procure the sending of, any advertising or promotional material, including any "junk mail," "chain letter," "spam," or any other similar solicitation</li>
              <li>To impersonate or attempt to impersonate Dozzy.au, a Dozzy.au employee, another user, or any other person or entity</li>
              <li>To engage in any other conduct that restricts or inhibits anyone's use or enjoyment of the Service, or which may harm Dozzy.au or users of the Service</li>
            </ul>
          </ContentSection>
          
          <ContentSection>
            <SectionTitle>3. Sleep Advice Disclaimer</SectionTitle>
            <p>The sleep plans and advice provided through the Service are based on guidelines from respected Australian organizations including Triple P, Rednose, Raising Children Network, and Tresillian. However, it is important to understand the following:</p>
            <ul>
              <li><strong>Not Medical Advice:</strong> The information provided through the Service is not medical advice and is not intended to replace professional medical advice, diagnosis, or treatment. Always seek the advice of your physician or other qualified health provider with any questions you may have regarding your child's health.</li>
              <li><strong>Parental Validation Required:</strong> Each child is unique, and what works for one child may not work for another. Parents must use their own judgment when implementing any sleep plan or advice provided through the Service. You are responsible for validating that the advice is appropriate for your child's specific circumstances.</li>
              <li><strong>Consultation with Healthcare Providers:</strong> If your child has any medical conditions or special needs, you should consult with appropriate healthcare providers before implementing any sleep plan.</li>
            </ul>
            <p>By using the Service, you acknowledge and agree that Dozzy.au is not responsible for any outcomes resulting from the implementation of sleep plans or advice provided through the Service.</p>
            
            <SubsectionTitle>3.1 Relationship with Third-Party Organizations</SubsectionTitle>
            <p>While we greatly respect and value the work of Triple P, Red Nose Australia, Raising Children Network, and Tresillian, Dozzy is an independent service and is not affiliated with, endorsed by, or partnered with these organizations.</p>
            <p>We've developed our AI system to incorporate publicly available guidelines and resources from these respected organizations, combining their valuable insights with our technology to create personalized sleep plans that are tailored to each child's unique needs.</p>
          </ContentSection>
          
          <ContentSection>
            <SectionTitle>4. Privacy and Data Protection</SectionTitle>
            
            <SubsectionTitle>4.1 Privacy Policy</SubsectionTitle>
            <p>Our Privacy Policy describes how we collect, use, and share information about you when you use our Service. By using the Service, you agree to the collection, use, and sharing of your information as described in our Privacy Policy.</p>
            
            <SubsectionTitle>4.2 Data Security</SubsectionTitle>
            <p>We take the security of your data seriously. All data provided by users, including child information and personally identifiable information (PII), is encrypted both in transit and at rest. We use industry-standard security measures to protect your data.</p>
            
            <SubsectionTitle>4.3 AWS Hosting</SubsectionTitle>
            <p>The Service is hosted on Amazon Web Services (AWS), a secure cloud services platform. AWS provides a high level of security and compliance certifications. Your data is stored in AWS data centers with multiple layers of operational and physical security.</p>
            
            <SubsectionTitle>4.4 Data Deletion</SubsectionTitle>
            <p>You may request the deletion of your account and associated data at any time by sending an email to support@dozzy.au. Upon receiving your request, we will delete your account and personal information from our active databases within a reasonable time period, typically within 30 days.</p>
            
            <SubsectionTitle>4.5 Analytics and Tracking</SubsectionTitle>
            <p>We use Google Analytics to collect information about how visitors use our website. Google Analytics uses cookies to track visitor interactions and collect information such as your IP address, browser type, operating system, referring website, pages visited, and time spent on our website. This information helps us improve our website and services. You can learn more about how Google Analytics collects and processes data by visiting "How Google uses data when you use our partners' sites or apps" (https://policies.google.com/technologies/partner-sites).</p>
          </ContentSection>
          
          <ContentSection>
            <SectionTitle>5. Payments and Refunds</SectionTitle>
            
            <SubsectionTitle>5.1 Pricing</SubsectionTitle>
            <p>The current pricing for our services is displayed on our Pricing page. We reserve the right to change our prices at any time, but changes will not affect orders that have already been placed.</p>
            
            <SubsectionTitle>5.2 Payment Processing</SubsectionTitle>
            <p>We use Stripe, a secure third-party payment processor, to process payments. By making a payment, you agree to Stripe's terms of service and privacy policy.</p>
            
            <SubsectionTitle>5.3 Refund Policy</SubsectionTitle>
            <p>We offer refunds within 7 days of purchase. However, due to the costs associated with generating personalized sleep programs, refunds are limited to 70% of the purchase price. The 30% non-refundable portion covers the costs of AI processing, data analysis, and program generation that have already been incurred.</p>
            <p>To request a refund, please contact support@dozzy.au with your order details.</p>
          </ContentSection>
          
          <ContentSection>
            <SectionTitle>6. Promotions and Special Offers</SectionTitle>
            
            <SubsectionTitle>6.1 Promotional Campaigns</SubsectionTitle>
            <p>We may occasionally run promotional campaigns offering discounts, special pricing, or additional features. All promotions are valid for a limited time only and are subject to specific terms that will be clearly communicated during the promotion.</p>
            
            <SubsectionTitle>6.2 Promotion Eligibility</SubsectionTitle>
            <p>Promotions may be limited to specific user groups, regions, or time periods. We reserve the right to modify, extend, or terminate any promotion at our discretion.</p>
            
            <SubsectionTitle>6.3 Promotion Codes</SubsectionTitle>
            <p>Promotion codes may have specific usage restrictions, including expiration dates, minimum purchase requirements, and limitations on combining with other offers. Unless otherwise stated, promotion codes are for one-time use only.</p>
            
            <SubsectionTitle>6.4 Changes to Promotions</SubsectionTitle>
            <p>We reserve the right to change the terms of any promotion or discontinue any promotion at any time without prior notice.</p>
          </ContentSection>
          
          <ContentSection>
            <SectionTitle>7. Gift Purchases</SectionTitle>
            
            <SubsectionTitle>7.1 Gift Process</SubsectionTitle>
            <p>When you purchase a gift plan for someone else, you will provide both your information as the purchaser and the recipient's information. The recipient will receive an email notification informing them of your gift, including any personal message you choose to include.</p>
            
            <SubsectionTitle>7.2 Gift Redemption</SubsectionTitle>
            <p>When a gift is purchased, an account is automatically created for the recipient. The recipient will receive an email with instructions on how to access their account and gifted sleep plan. Gift plans must be redeemed within 12 months of purchase.</p>
            
            <SubsectionTitle>7.3 Gift Refunds</SubsectionTitle>
            <p>Our standard refund policy applies to gift purchases. Only the gift purchaser may request a refund, not the recipient.</p>
            
            <SubsectionTitle>7.4 Gift Recipient Privacy</SubsectionTitle>
            <p>When you provide a recipient's information for a gift purchase, you confirm that you have the recipient's consent to share their information with us. We will only use the recipient's information to deliver the gift and provide our services.</p>
          </ContentSection>
          
          <ContentSection>
            <SectionTitle>8. Intellectual Property</SectionTitle>
            
            <SubsectionTitle>8.1 Our Intellectual Property</SubsectionTitle>
            <p>The Service and its original content, features, and functionality are and will remain the exclusive property of Dozzy.au and its licensors. The Service is protected by copyright, trademark, and other laws of Australia and foreign countries.</p>
            <p>Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of Dozzy.au.</p>
            
            <SubsectionTitle>8.2 Your Content</SubsectionTitle>
            <p>By submitting information about your child and family through the Service, you grant us the right to use this information to generate personalized sleep plans and provide our services to you.</p>
            <p>You represent and warrant that you have the right to submit this information and that it does not violate any third-party rights or applicable laws.</p>
          </ContentSection>
          
          <ContentSection>
            <SectionTitle>9. User Content and Feedback</SectionTitle>
            
            <SubsectionTitle>9.1 User Content</SubsectionTitle>
            <p>You may have the opportunity to submit feedback, suggestions, or other content to us. By submitting such content, you grant us a non-exclusive, worldwide, royalty-free license to use, reproduce, modify, adapt, publish, translate, and distribute your content.</p>
            
            <SubsectionTitle>9.2 Prohibited Content</SubsectionTitle>
            <p>You agree not to submit content that is unlawful, defamatory, harassing, threatening, or that infringes on the intellectual property rights or privacy of any third party.</p>
          </ContentSection>
          
          <ContentSection>
            <SectionTitle>10. Account Security</SectionTitle>
            
            <SubsectionTitle>10.1 Account Responsibility</SubsectionTitle>
            <p>You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.</p>
            
            <SubsectionTitle>10.2 Security Breach</SubsectionTitle>
            <p>You agree to immediately notify us of any unauthorized use of your account or any other breach of security.</p>
          </ContentSection>
          
          <ContentSection>
            <SectionTitle>11. Service Modifications</SectionTitle>
            
            <SubsectionTitle>11.1 Changes to the Service</SubsectionTitle>
            <p>We reserve the right to modify, suspend, or discontinue any part of the Service at any time, with or without notice.</p>
            
            <SubsectionTitle>11.2 Feature Updates</SubsectionTitle>
            <p>We may add, modify, or remove features from the Service at our discretion. We are not liable to you or any third party for any modification, suspension, or discontinuation of the Service.</p>
          </ContentSection>
          
          <ContentSection>
            <SectionTitle>12. Termination</SectionTitle>
            <p>We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms.</p>
            <p>Upon termination, your right to use the Service will immediately cease. If you wish to terminate your account, you may simply discontinue using the Service or contact support@dozzy.au to request account deletion.</p>
          </ContentSection>
          
          <ContentSection>
            <SectionTitle>13. Limitation of Liability</SectionTitle>
            <p>In no event shall Dozzy.au, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from:</p>
            <ul>
              <li>Your access to or use of or inability to access or use the Service;</li>
              <li>Any conduct or content of any third party on the Service;</li>
              <li>Any content obtained from the Service; and</li>
              <li>Unauthorized access, use or alteration of your transmissions or content</li>
            </ul>
            <p>To the maximum extent permitted by applicable law, our total liability to you for any damages shall not exceed the amount paid by you to Dozzy.au for the Service during the three (3) months immediately preceding the date of the claim.</p>
          </ContentSection>
          
          <ContentSection>
            <SectionTitle>14. Disclaimer</SectionTitle>
            <p>Your use of the Service is at your sole risk. The Service is provided on an "AS IS" and "AS AVAILABLE" basis. The Service is provided without warranties of any kind, whether express or implied, including, but not limited to, implied warranties of merchantability, fitness for a particular purpose, non-infringement or course of performance.</p>
            <p>Dozzy.au does not warrant that a) the Service will function uninterrupted, secure or available at any particular time or location; b) any errors or defects will be corrected; c) the Service is free of viruses or other harmful components; or d) the results of using the Service will meet your requirements.</p>
          </ContentSection>
          
          <ContentSection>
            <SectionTitle>15. Dispute Resolution</SectionTitle>
            
            <SubsectionTitle>15.1 Informal Resolution</SubsectionTitle>
            <p>If you have a dispute with us, you agree to contact us first and attempt to resolve the dispute informally.</p>
            
            <SubsectionTitle>15.2 Governing Law</SubsectionTitle>
            <p>Any disputes arising out of or related to these Terms shall be governed by the laws of Australia, without regard to its conflict of law provisions.</p>
            
            <SubsectionTitle>15.3 Jurisdiction</SubsectionTitle>
            <p>You agree that any legal action related to these Terms shall be filed exclusively in the courts located in Australia, and you consent to the jurisdiction of such courts.</p>
          </ContentSection>
          
          <ContentSection>
            <SectionTitle>16. Changes to Terms</SectionTitle>
            <p>We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.</p>
            <p>By continuing to access or use our Service after any revisions become effective, you agree to be bound by the revised terms. If you do not agree to the new terms, you are no longer authorized to use the Service.</p>
          </ContentSection>
          
          <ContentSection>
            <SectionTitle>17. Contact Us</SectionTitle>
            <p>If you have any questions about these Terms, please contact us at:</p>
            <p>Email: support@dozzy.au</p>
          </ContentSection>
        </PageContainer>
      </div>
    </>
  );
};

export default TermsAndConditionsPage;
